/* ============================================================
   RECONCILER — the single brain of reactive playback.
   ------------------------------------------------------------
   One pure function: given the DESIRED frame (computeDesired) and
   the ACTUAL state of the two YouTube buffers, return the minimal
   set of effects to converge. Run on EVERY event (player state
   change, time tick, queue/intent change); it's idempotent, so
   re-running issues only what's still needed. Transitions,
   autoplay, live-tracking, pre-buffering and the cover all fall
   out of "desired vs actual" — there are no per-path branches.
   ============================================================ */
import type { DesiredFrame } from './live'

// YouTube player states we key on.
const ENDED = 0
const PLAYING = 1
const BUFFERING = 3

// Foreground is "on the line" within this offset; correct drift past the smaller one.
export const ON_TARGET_TOL_S = 1.5
export const DRIFT_SEEK_THRESHOLD_S = 1

/** What the reconciler observes about the player. PURE content convergence — it knows
 *  nothing about covers/bumpers; the masking cover is a tune-intent derivation owned by the
 *  executor (see src/tv/cover.ts). */
export interface PlayerActual {
  fgId: string | null // foreground video id (null = none / showing an ad)
  fgState: number // YouTube state of the foreground frame
  fgTime: number // foreground currentTime (seconds)
  bgId: string | null // video id currently prepared in the background frame
  bgReady: boolean // background buffered + ready to swap in
}

/** The minimal effects to converge actual → desired. All optional/idempotent. */
export interface ReconcileResult {
  prepare: string | null // pre-buffer this video id into the background
  swap: boolean // make the background frame the foreground (atomic)
  seekSec: number | null // seek the foreground to this offset
  play: boolean // ensure the foreground is playing
  hideFrames: boolean // ad/component: hide both video frames (the scene/component IS the content)
  ad: { scene: string; params: Record<string, unknown>; atSec: number } | null
  component: { component: string; atSec: number } | null // a component segment IS the picture
  onTarget: boolean // foreground is the desired video, genuinely playing, on-time
}

const NOOP: ReconcileResult = {
  prepare: null,
  swap: false,
  seekSec: null,
  play: false,
  hideFrames: false,
  ad: null,
  component: null,
  onTarget: false,
}

export function reconcile(desired: DesiredFrame, a: PlayerActual): ReconcileResult {
  // Nothing should be on screen → executor pauses/blanks; cover hidden.
  if (desired.kind === 'off') return { ...NOOP }

  // A scene's own 3D content IS the picture: hide the video frames, show the scene at atSec.
  if (desired.kind === 'scene') {
    return { ...NOOP, hideFrames: true, ad: { scene: desired.scene, params: desired.params, atSec: desired.atSec } }
  }

  // A component segment IS the picture: hide the video frames, mount the registered component.
  if (desired.kind === 'component') {
    return { ...NOOP, hideFrames: true, component: { component: desired.component, atSec: desired.atSec } }
  }

  // desired.kind === 'video'
  const genuinelyPlaying = a.fgState === PLAYING

  if (a.fgId === desired.id) {
    // Right video in the foreground — maintain. A TRACKED (live) frame chases the
    // moving offset, so correct drift with a seek and only count on-target when on
    // the line. An untracked (manual) frame just needs to be playing — it lands at
    // the offset on the swap and then plays out, so any offset counts as on-target
    // and we never seek it back. Never START a cover for a drift; only keep one
    // already up until on-target. CRITICAL: only seek a GENUINELY-PLAYING frame —
    // while BUFFERING, currentTime is frozen so wall-clock drift grows every tick;
    // seeking then just restarts the buffer and never lets it recover (a seek-storm
    // that pegs the main thread). Let it settle, then one seek corrects it.
    const drift = Math.abs(a.fgTime - desired.atSec)
    const onTarget = genuinelyPlaying && (!desired.track || drift <= ON_TARGET_TOL_S)
    return {
      ...NOOP,
      // Maintain by playing — EXCEPT a frame that has ENDED. Calling play() on an ended
      // YouTube frame restarts it from 0 (the ~0.5s "replay of its own start" flash before
      // the next video), and in live mode that restart spikes drift → seek-back → ends
      // again → a boundary thrash loop. Leave an ended frame be; the advance (live line /
      // manual index) makes it the no-longer-desired frame next tick and we swap cleanly.
      play: a.fgState !== ENDED,
      seekSec: desired.track && genuinelyPlaying && drift > DRIFT_SEEK_THRESHOLD_S ? desired.atSec : null,
      onTarget,
    }
  }

  // Foreground is NOT the desired video → a transition. (The masking cover, if any, is the
  // executor's tune-intent derivation — not the reconciler's concern.)

  // Pre-buffered in the background → atomic swap + seek to the KNOWN offset + play.
  if (a.bgId === desired.id && a.bgReady) {
    return { ...NOOP, swap: true, seekSec: desired.atSec, play: true }
  }

  // Not loaded anywhere (or background not ready yet) → pre-buffer it.
  return { ...NOOP, prepare: desired.id }
}

// re-exported for the buffering check in tests/consumers
export const YT_PLAYING = PLAYING
export const YT_BUFFERING = BUFFERING
